import { z } from 'zod';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  RecommendedProductDTOType,
  RecommendedFacetsDTOSchema,
  RecommendedFacetsDTOType,
  RecommendedProductsDTOSchema,
  RecommendedProductsDTOType,
} from './schemas';

import { overrideFetchBaseQuery } from '../utils/overrideFetchBaseQuery';
import type { RecommendationRequestPayloadType } from './types';
// import { setMainProduct, setSelectedRecommendationFilterTab } from './slice';
import { baseQueryWithZodValidation } from '../utils/baseQueryWithZodValidation';

export const stylesSearchApi = createApi({
  reducerPath: 'stylesSearchApi',
  baseQuery: baseQueryWithZodValidation(overrideFetchBaseQuery({}), {
    passThroughChecks: false,
  }),
  endpoints: builder => ({
    getProductDepthByStyleNumbers: builder.query<
      {
        depthByStyleNumber: Record<
          string,
          {
            inventoryDepth: number;
            jitDepth: number;
            product: RecommendedProductDTOType | null;
          }
        >;
      },
      { styleNumbers: Array<string> }
    >({
      query: ({ styleNumbers }) => ({
        url: 'search/styles',
        method: 'POST',
        body: {
          // searchType: 'FREETEXT',
          selectedFacets: {
            'LK Style Number': styleNumbers,
          },
          // page: 1,
          // size: Math.max(styleNumbers.length, 10),
        },
      }),
      extraOptions: {
        zodSchema: z.object({
          products: RecommendedProductsDTOSchema,
          facets: RecommendedFacetsDTOSchema,
          found: z.number().optional(),
          hasMore: z.boolean().optional(),
        }),
      },
      transformResponse: (
        baseQueryReturnValue: {
          products: RecommendedProductsDTOType;
          facets: RecommendedFacetsDTOType;
          found?: number;
          hasMore?: boolean;
        },
        _meta,
        arg,
      ) => {
        return {
          depthByStyleNumber: arg.styleNumbers.reduce<
            Record<
              string,
              {
                inventoryDepth: number;
                jitDepth: number;
                product: RecommendedProductDTOType | null;
              }
            >
          >((accumulator, styleNumber) => {
            const product =
              baseQueryReturnValue.products?.find(item => item.lk_style_number === styleNumber) ??
              null;

            const countVariantsByAvailability = (availabilityType: 'store' | 'jit') =>
              product?.variants.reduce((count, variant) => {
                const availabilityValues = (variant.availability ?? [])
                  .map(value => String(value).toLowerCase().trim())
                  .filter(Boolean);

                return availabilityValues.includes(availabilityType) ? count + 1 : count;
              }, 0) ?? 0;

            const inventoryDepthFromSummary =
              typeof product?.size_depth_in_lk === 'number' ? product.size_depth_in_lk : undefined;
            const totalDepthFromSummary =
              typeof product?.size_depth_in_lk_or_jit === 'number'
                ? product.size_depth_in_lk_or_jit
                : undefined;
            const inventoryDepth =
              inventoryDepthFromSummary ?? countVariantsByAvailability('store');
            const jitDepth =
              typeof totalDepthFromSummary === 'number'
                ? Math.max(totalDepthFromSummary - inventoryDepth, 0)
                : countVariantsByAvailability('jit');

            accumulator[styleNumber] = {
              product,
              inventoryDepth,
              jitDepth,
            };

            return accumulator;
          }, {}),
        };
      },
    }),

    searchStyles: builder.infiniteQuery<
      {
        products: RecommendedProductsDTOType;
        facets: RecommendedFacetsDTOType;
        found?: number;
        hasMore?: boolean;
      },
      RecommendationRequestPayloadType,
      {
        page: number;
        size: number;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 1,
          size: 25,
        },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (typeof lastPage.hasMore === 'boolean') {
            return lastPage.hasMore
              ? { page: lastPageParam.page + 1, size: lastPageParam.size }
              : undefined;
          }

          if (
            lastPage.products &&
            Array.isArray(lastPage.products) &&
            lastPage.products.length > 0
          ) {
            return { page: lastPageParam.page + 1, size: lastPageParam.size };
          }
          return undefined;
        },
      },
      query: ({ pageParam, queryArg }) => {
        const body = {
          ...queryArg,
          page: pageParam.page,
          size: pageParam.size,
        };

        return {
          url: 'search/styles',
          method: 'POST',
          body,
        };
      },
      extraOptions: {
        zodSchema: z.object({
          products: RecommendedProductsDTOSchema,
          facets: RecommendedFacetsDTOSchema,
          found: z.number().optional(),
          hasMore: z.boolean().optional(),
        }),
      },
      transformResponse: (baseQueryReturnValue: {
        products: RecommendedProductsDTOType;
        facets: RecommendedFacetsDTOType;
        found?: number;
        hasMore?: boolean;
      }) => {
        return {
          products: baseQueryReturnValue.products,
          facets: baseQueryReturnValue.facets
            .sort((a, b) => {
              if (a.field_name === 'In-Stock Facilities') return -1;
              if (b.field_name === 'In-Stock Facilities') return 1;
              return a.field_name.localeCompare(b.field_name);
            })
            .map(f => ({
              field_name: f.field_name,
              counts: f.counts?.sort((a, b) => {
                if (b.count === a.count) {
                  return a.value.localeCompare(b.value);
                }
                return b.count - a.count;
              }),
            })),
          found: baseQueryReturnValue.found,
          hasMore: baseQueryReturnValue.hasMore,
        };
      },
    }),

    typeAheadApi: builder.query<
      {
        products: RecommendedProductsDTOType;
      },
      { q: string }
    >({
      query: ({ q }) => ({
        url: 'search/styles/typeahead',
        params: { q: q.trim() },
      }),
      extraOptions: {
        zodSchema: z.object({
          products: RecommendedProductsDTOSchema,
        }),
      },
    }),

    getRecommendations: builder.mutation<
      {
        products: RecommendedProductsDTOType;
        facets: RecommendedFacetsDTOType;
        recommendationRequestPayload: RecommendationRequestPayloadType;
      },
      RecommendationRequestPayloadType
    >({
      query: body => {
        const size =
          'size' in body && typeof body.size === 'number' && Number.isFinite(body.size)
            ? body.size
            : 25;

        return {
          url: 'search/styles',
          method: 'POST',
          body: {
            ...body,
            size,
          },
        };
      },
      extraOptions: {
        zodSchema: z.object({
          products: RecommendedProductsDTOSchema,
          facets: RecommendedFacetsDTOSchema,
        }),
      },

      transformResponse: (
        baseQueryReturnValue: {
          products: RecommendedProductsDTOType;
          facets: RecommendedFacetsDTOType;
        },
        _,
        arg,
      ) => {
        return {
          ...baseQueryReturnValue,
          facets: baseQueryReturnValue.facets
            .sort((a, b) => {
              if (a.field_name === 'In-Stock Facilities') return -1;
              if (b.field_name === 'In-Stock Facilities') return 1;
              return a.field_name.localeCompare(b.field_name);
            })
            .map(f => ({
              field_name: f.field_name,
              counts: f.counts?.sort((a, b) => {
                if (b.count === a.count) {
                  return a.value.localeCompare(b.value);
                }
                return b.count - a.count;
              }),
            })),
          recommendationRequestPayload: arg,
        };
      },
    }),
  }),
});

export const {
  useGetProductDepthByStyleNumbersQuery,
  useLazyGetProductDepthByStyleNumbersQuery,
  useSearchStylesInfiniteQuery,
  useLazyTypeAheadApiQuery,
  useGetRecommendationsMutation,
} = stylesSearchApi;
